import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import HostPage from './pages/HostPage';
import DisplayPage from './pages/DisplayPage';
import ParticipantPage from './pages/ParticipantPage';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import FeedbackButton from './components/common/FeedbackButton';
import ProtectedRoute from './components/common/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import RebusHostPage from './pages/RebusHostPage';
import RebusTeamPage from './pages/RebusTeamPage';
import RebusStationPage from './pages/RebusStationPage';
import MentiHostPage from './pages/MentiHostPage';
import MentiParticipantPage from './pages/MentiParticipantPage';
// Expose seed/admin functions for browser console (dev only)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  import('./data/goosechaseRebus').then(({ seedGoosechaseRebus, seedGoosechaseSession }) => {
    window.seedGoosechaseRebus = seedGoosechaseRebus;
    window.seedGoosechaseSession = seedGoosechaseSession;
  });

  import('./data/danmarkFerjeMikkelchase').then(({ seedFerjeMikkelchase }) => {
    window.seedFerjeMikkelchase = seedFerjeMikkelchase;
  });
  import('./data/danmarkLalandiaMikkelchase').then(({ seedLalandiaMikkelchase }) => {
    window.seedLalandiaMikkelchase = seedLalandiaMikkelchase;
  });
  import('./data/danmarkLegolandMikkelchase').then(({ seedLegolandMikkelchase }) => {
    window.seedLegolandMikkelchase = seedLegolandMikkelchase;
  });
  // paskekviss2026 og paskekvissFamilie2026 seed-filer manglar — bring tilbake når dei finst

  import('./services/firebase').then(({ db }) => {
    import('firebase/firestore').then(({ doc, updateDoc, collection, getDocs, deleteDoc }) => {
      window.setSessionStatus = async (sessionId, status) => {
        await updateDoc(doc(db, 'rebusSessions', sessionId), { status });
      };
      window.deleteSession = async (sessionId) => {
        await deleteDoc(doc(db, 'rebusSessions', sessionId));
      };
      window.deleteWaitingSessions = async () => {
        const snap = await getDocs(collection(db, 'rebusSessions'));
        let count = 0;
        for (const d of snap.docs) {
          if (d.data().status === 'waiting') {
            await deleteDoc(d.ref);
            count++;
          }
        }
        return count;
      };
    });
  });
}

// Hide feedback button on participant/team pages
function FeedbackButtonWrapper() {
  const { pathname } = useLocation();
  const hiddenPaths = ['/mikkelchase/join', '/mikkelchase/team', '/mikkelchase/station', '/join', '/session', '/menti/join', '/menti/participant'];
  if (hiddenPaths.some(p => pathname.startsWith(p))) return null;
  return <FeedbackButton />;
}

// Error boundary to catch runtime crashes and show a useful message
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      const stack = this.state.error?.stack || '';
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: 'system-ui' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Noko gjekk gale</h1>
          <p style={{ color: '#666', marginBottom: '1rem' }}>{this.state.error?.message || 'Ukjend feil'}</p>
          <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.7rem', maxWidth: '90vw', overflow: 'auto', marginBottom: '1rem', maxHeight: '40vh', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{stack}</pre>
          <button
            onClick={() => { localStorage.clear(); window.location.reload(); }}
            style={{ padding: '0.75rem 1.5rem', background: '#0d9488', color: 'white', border: 'none', borderRadius: '0.5rem', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Prøv igjen (tøm cache)
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/host" element={<ProtectedRoute><HostPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/display/:sessionId" element={<DisplayPage />} />
        <Route path="/join" element={<ParticipantPage />} />
        <Route path="/session/:sessionId" element={<ParticipantPage />} />
        <Route path="/mikkelchase" element={<ProtectedRoute><RebusHostPage /></ProtectedRoute>} />
        <Route path="/mikkelchase/session/:sessionId" element={<ProtectedRoute><RebusHostPage /></ProtectedRoute>} />
        <Route path="/mikkelchase/join" element={<RebusTeamPage />} />
        <Route path="/mikkelchase/team/:sessionId" element={<RebusTeamPage />} />
        <Route path="/mikkelchase/station/:sessionId" element={<RebusStationPage />} />
        <Route path="/menti" element={<ProtectedRoute><MentiHostPage /></ProtectedRoute>} />
        <Route path="/menti/session/:sessionId" element={<ProtectedRoute><MentiHostPage /></ProtectedRoute>} />
        <Route path="/menti/join" element={<MentiParticipantPage />} />
        <Route path="/menti/participant/:sessionId" element={<MentiParticipantPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <FeedbackButtonWrapper />
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App
